import React, {MouseEventHandler, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {Cell, Row} from 'react-table';

import {useContextMenu} from '@ELib/functions/contextMenu';
import {classy} from '@ELib/utilities/HelperFunctions';
import {TFunction} from '@ELib/utilities/i18next';

import {TBodySubRows} from './TBodySubRows';
import {
  ETableActiveItem,
  ETableCellMode,
  ETableRow,
  ETableRowActionHandler,
  ETableRowClickHandler,
  ETableRowDragHandler,
  isCellObject,
} from './types';
import {isActiveItem} from './utilities';

// code moved from webapp, disable conflicting rules in webapp-lib for now.
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-argument */

export const NOT_FOUND = '—';

export const TBodyRow = ({
  row,
  parentRow,
  subRowIndex,
  isSelected = false,
  forceCheckboxes = false,
  activeItem,
  isDraggable,
  onRowClick,
  onRowDragStart,
  onRowDragEnd,
  actions,
}: {
  row: Row<ETableRow>;
  parentRow?: Row<ETableRow>;
  subRowIndex?: number;
  isSelected?: boolean;
  forceCheckboxes?: boolean;
  activeItem: ETableActiveItem | undefined;
  isDraggable: boolean;
  onRowClick: ETableRowClickHandler;
  onRowDragStart?: ETableRowDragHandler;
  onRowDragEnd?: ETableRowDragHandler;
  actions?: ETableRowActionHandler;
}): React.ReactElement => {
  const rowActions = useMemo(() => actions?.(row) ?? [], [actions, row]);

  const {t} = useTranslation('common');

  const onRowDoubleClick = useCallback(() => {
    const inactive = rowActions[0]?.inactive;
    if (!inactive) {
      rowActions[0]?.callback?.(row.original.raw);
    }
  }, [row.original.raw, rowActions]);

  const {contextMenuCallbacks, contextMenu} = useContextMenu(
    rowActions,
    row.original.raw
  );

  const styleType = row.original.options?.styleType;

  /** Whether a row can be activated (i.e. it changes its styling on click). */
  const isActivatable = styleType !== 'info' && styleType !== 'header';

  const onClick = useCallback(
    (evt: React.MouseEvent) => {
      if (!isActivatable) {
        return;
      }
      if (parentRow) {
        onRowClick(evt, parentRow, row);
      } else {
        onRowClick(evt, row);
      }
    },
    [isActivatable, onRowClick, parentRow, row]
  );

  const onContextMenu: MouseEventHandler<HTMLTableRowElement> = useCallback(
    (event) => {
      if (!isSelected) {
        onClick(event);
      }
      if (rowActions.length) {
        contextMenuCallbacks.onContextMenu(event);
      }
    },
    [isSelected, rowActions, onClick, contextMenuCallbacks]
  );

  const onDragStart = useCallback(
    (dragEvent: React.DragEvent<HTMLTableRowElement>) => {
      onRowDragStart?.({dragEvent, activeRow: row});
    },
    [onRowDragStart, row]
  );

  const onDragEnd = useCallback(
    (dragEvent: React.DragEvent<HTMLTableRowElement>) =>
      onRowDragEnd?.({dragEvent}),
    [onRowDragEnd]
  );

  const isSubRow = subRowIndex !== undefined;

  if (isSubRow) {
    // The current row is a sub row; patch the value getters accordingly.
    // Note that this has to be done before looping through all the cells to add
    // them to the table. Otherwise, the colspan calculation would not work correctly.
    row.cells.forEach((cell) => {
      cell.value = cell.column.accessor?.(row.original, subRowIndex);
    });
  }

  return (
    <>
      <tr
        {...row.getRowProps()}
        className={classy({
          noHover:
            !isActivatable ||
            row.original.options?.interactionRestrictionLevel ===
              'noHoverEffect',
          active:
            isActivatable &&
            row.original.options?.interactionRestrictionLevel !==
              'notActivatable' &&
            row.original.options?.interactionRestrictionLevel !==
              'noHoverEffect' &&
            isActiveItem(row.original.id, activeItem),
          selected: isSelected && row.original.options?.isSelectable !== false,
          forceCheckboxes,
          styleInfo: styleType === 'info',
          styleImportant: styleType === 'important',
          styleWarning: styleType === 'warning',
          styleDanger: styleType === 'danger',
          styleHiddenBorderBottom: styleType === 'hide-border-bottom',
          styleDisabled: styleType === 'disabled',
          styleHeader: styleType === 'header',
          styleBold: styleType === 'bold',
        })}
        data-id={row.original.id}
        draggable={isDraggable}
        onClick={onClick}
        onContextMenu={onContextMenu}
        onDoubleClick={onRowDoubleClick}
        onDragEnd={onDragEnd}
        onDragStart={onDragStart}
      >
        {contextMenu}
        {row.cells.map((cell, cellIndex) => {
          if (cell.value === null) {
            // cells with a `null` value are used with colspan and should not render
            return null;
          }
          const cellObject = isCellObject(cell.value);
          return (
            // react-table adds a key with getCellProps
            // eslint-disable-next-line react/jsx-key
            <td
              {...cell.getCellProps({
                className: classy({
                  textSelectable: cellObject && cell.value.textSelectable,
                  alignCenter: cellObject && cell.value.align === 'center',
                  alignRight: cellObject && cell.value.align === 'right',
                  selection: cell.column.id === '_selection',
                  expansion: cell.column.id === '_expander',
                  styleHiddenBorderBottom:
                    (cellObject &&
                      cell.value.styleType === 'hide-border-bottom') ||
                    (cell.column.id === '_expander' &&
                      row.original.options?.expansionStyleType ===
                        'hide-border-bottom'),
                  styleTotal:
                    (cellObject && cell.value.styleType === 'total') ||
                    (cell.column.id === '_expander' &&
                      row.original.options?.expansionStyleType === 'total'),
                  ...getCellModeClasses(cell),
                }),
              })}
              colSpan={getColSpan(row.cells, cellIndex)}
              data-testid={`ETable-cell-${cell.column.id}`}
            >
              {customCellRenderer(
                cell,
                {
                  subRow: isSubRow ? row : undefined,
                },
                t
              )}
            </td>
          );
        })}
      </tr>
      {row.original.options?.expansion && row.isExpanded && (
        <TBodySubRows
          activeItem={activeItem}
          parent={row}
          onRowClick={onRowClick}
        />
      )}
    </>
  );
};

const getCellModeClasses = (cell: Cell<ETableRow>): Record<string, boolean> => {
  /** Flag whether the cell is special, e.g. a checkbox or extra action cell. */
  const isSpecialCell = cell.column.id.startsWith('_');
  if (isSpecialCell) {
    return {modeMin: true};
  }
  const cellMode: ETableCellMode =
    (isCellObject(cell.value) && cell.value.mode) || 'truncate';
  return {
    modeMin: cellMode === 'min',
    modeTruncate: cellMode === 'truncate',
    modeBreak: cellMode === 'break',
  };
};

const customCellRenderer = (
  cell: Cell<ETableRow>,
  {subRow}: {subRow?: Row<ETableRow>},
  t: TFunction
) => {
  if (cell.value === undefined) {
    if (subRow && cell.column.SubCell) {
      // the current cell is within a sub row and from a special column
      return cell.column.SubCell(subRow);
    }
    const renderedCell = cell.render('Cell');
    if ((renderedCell as any).type?.displayName === 'defaultRenderer') {
      // cell value was set to undefined but no corresponding renderer was defined
      return NOT_FOUND;
    }
    // fallback to column cell component if no cell value is given from the row
    // e.g. for the checkboxes
    return renderedCell;
  }
  const rawCellValue = isCellObject(cell.value) ? cell.value.cell : cell.value;
  if (rawCellValue instanceof Date) {
    return t('dateFormat.withoutTime', {date: rawCellValue});
  }
  return rawCellValue ?? NOT_FOUND;
};

/** Sums the consecutive cells with value `null` that are located directly after the passed `cellIndex`. */
export const getColSpan = (
  cells: {value: any}[],
  cellIndex: number
): number => {
  let colSpan = 1;
  for (let index = cellIndex + 1; index < cells.length; index++) {
    if (cells[index]!.value === null) {
      colSpan += 1;
    } else {
      break;
    }
  }
  return colSpan;
};
